import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Boxes,
  Package,
  Puzzle,
  ReceiptText,
  CalendarDays,
  MapPin,
  RefreshCcw,
  Users,
  Truck,
  LogOut,
  Tag,
  Bell,
  MessageCircle,
  Building2,
  Home,
  Settings,
  BarChart3,
  ListCheck,
  Globe,
  Repeat,
  CalendarClock,
  Layers,
  FileText,
  Briefcase,
  SquarePen,
  Sparkles
} from "lucide-react";
import { useAuth } from "@context/AuthContext";
import { useMarketplace } from "@context/MarketplaceContext";
import "./css/sidebar.css";

// Public website URL the "Edit Website (CMS)" launcher opens (with the admin's
// token so the in-place visual editor unlocks). Resolved from the admin hostname
// at runtime so the PRODUCTION admin (admin.washywash.com) opens the live site
// and the STAGING admin (*staging*) opens the staging site — the same build
// serves both, so a single baked env var can't tell them apart. An explicit
// REACT_APP_WEBSITE_URL still wins when set.
function websiteUrl() {
  if (process.env.REACT_APP_WEBSITE_URL) return process.env.REACT_APP_WEBSITE_URL;
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return /staging/i.test(host)
    ? "https://ecommercestaging.washywash.com"
    : "https://washywash.com";
}

function openCmsEditor() {
  let token = null;
  try {
    token = JSON.parse(localStorage.getItem("ww_admin_auth") || "{}")?.token || null;
  } catch {
    token = null;
  }
  const url = new URL(websiteUrl());
  url.searchParams.set("edit", "1");
  if (token) url.searchParams.set("cms_token", token);
  window.open(url.toString(), "_blank", "noopener");
}

const groups = [
  {
    title: "Analytics",
    items: [
      {
        to: "/analytics/ask-ai",
        label: "Ask AI",
        icon: Sparkles,
        // WN-0233: dedicated permission, not bypassed for admins — only groups
        // explicitly granted access/ask-ai (e.g. super_admin_marketplace) see it.
        strictPermission: "access/ask-ai"
      }
    ]
  },
  {
    title: "Navigation",
    items: [
      //{
      //  to: "/dashboard",
      //  label: "Dashboard",
      //  icon: LayoutDashboard,
      //  permission: "access/dashboard"
      //},
      {
        to: "/catalog",
        label: "Catalog",
        icon: Boxes,
        permission: "access/catalog"
      },
      {
        to: "/catalog/products",
        label: "Products",
        icon: Package,
        permission: "access/products"
      },
      {
        to: "/landing-page",
        label: "Landing Page",
        icon: Puzzle,
        permission: "access/landing-page"
      }
    ]
  },
  {
    title: "Sales",
    items: [
      {
        to: "/orders",
        label: "Orders",
        icon: ReceiptText,
        permission: "access/orders"
      },
      {
        to: "/sales/by-day",
        label: "Get Orders by Day",
        icon: CalendarDays,
        permission: "access/sales-by-day"
      },
      {
        to: "/sales/map",
        label: "Orders On Map",
        icon: CalendarDays,
        permission: "access/sales-by-day"
      },
      {
        // Drivers see their own assigned orders; admins/marketplace admins can
        // pick a driver to view. Visible to both.
        to: "/sales/driver",
        label: "Driver",
        icon: Truck,
        driverOrAdmin: true,
        permission: "access/sales-by-day",
      },
      {
        to: "/sales/map-zones",
        label: "Map Zones",
        icon: MapPin,
        marketplaceAdminOnly: true,
      },
      {
        to: "/sales/cleaning-orders-by-two-days",
        label: "Get Cleaning Orders by Two Day",
        icon: CalendarDays,
        permission: "access/cleaning-orders"
      },

      {
        to: "/sales/furniture-car-cleaning-orders-by-two-days",
        label: "Furniture & Car Cleaning",
        icon: CalendarDays,
        permission: "access/carpets-orders"
      },


      //{
      //  to: "/sales/map",
      //  label: "Get Orders on Map",
      //  icon: MapPin,
      //  permission: "access/orders-map"
      //},
      {
        to: "/orders?order_status_id=22",
        label: "Reschedule Orders",
        icon: RefreshCcw,
        permission: "access/reschedule-orders"
      },
      {
        to: "/order-type-description",
        label: "Order Type description",
        icon: Settings,
        permission: "access/order-type-description"
      },
      {
        to: "/sales/sales-by-user",
        label: "Sales by User",
        icon: BarChart3
      },
      {
        to: "/sales/time-slot-report",
        label: "Time Slot report",
        icon: CalendarClock
      },
      {
        to: "/cc-payments",
        label: "Card Payments",
        icon: BarChart3,
        onlyUsers: ["taylorsuccessor"]
      }
    ]
  },
  {
    title: "Customers",
    items: [
      {
        to: "/customers",
        label: "Customers",
        icon: Users,
        permission: "access/customers"
      },
      {
        to: "/customer-groups",
        label: "Customer Groups",
        icon: Users,
        permission: "access/customer-groups"
      },
      {
        to: "/customer-feedback",
        label: "Customer Feedback",
        icon: MessageCircle,
        permission: "access/customer-feedback"
      }
    ]
  },
  {
    title: "Marketing",
    items: [
      {
        to: "/marketing/coupons",
        label: "Coupons",
        icon: Tag,
        permission: "access/coupons"
      },
      {
        to: "/notifications/notification",
        label: "Custom Notification",
        icon: Bell,
        permission: "access/custom-notification"
      }
    ]
  },
  {
    title: "Website",
    items: [
      {
        external: true,
        onClick: openCmsEditor,
        label: "Edit Website (CMS)",
        icon: SquarePen
      },
      {
        to: "/website/subscription-plans",
        label: "Subscription Plans",
        icon: Globe,
        permission: "access/website-subscription-plans"
      },
      {
        to: "/website/subscriptions",
        label: "Subscriptions",
        icon: Repeat,
        permission: "access/website-subscriptions"
      },
      {
        to: "/website/service-scheduling",
        label: "Service Scheduling",
        icon: CalendarClock,
        permission: "access/website-service-scheduling"
      },
      {
        to: "/website/product-variants",
        label: "Product Variants",
        icon: Layers,
        permission: "access/website-product-variants"
      },
      {
        to: "/website/shop-shipping",
        label: "Shop Shipping",
        icon: Truck,
        permission: "access/website-shop-shipping"
      },
      {
        to: "/website/delivery-fees",
        label: "Delivery Fees",
        icon: Truck,
        permission: "access/website-delivery-fees"
      },
      {
        to: "/website/product-content",
        label: "Product Content",
        icon: FileText,
        permission: "access/website-product-content"
      },
      {
        to: "/website/product-placement",
        label: "Product Placement",
        icon: MapPin,
        permission: "access/website-product-placement"
      },
      {
        to: "/website/b2b-leads",
        label: "B2B Leads",
        icon: Briefcase,
        permission: "access/website-b2b-leads"
      }
    ]
  },
  {
    title: "Logistics",
    items: [
      {
        to: "/my-deliveries",
        label: "My Deliveries",
        icon: Truck,
        agentOnly: true,
      },
      {
        to: "/logistics/smart-assign",
        label: "Assign Orders",
        icon: Sparkles,
      },
      {
        to: "/logistics/sorting",
        label: "Sorting Items",
        icon: ListCheck,
        // permission: "access/order-sorting" // Optional permission check
      },
      {
        to: "/logistics/time-slots",
        label: "Time Slots",
        icon: Truck,
        permission: "access/time-slots"
      },
      {
        to: "/logistics/time-slots-settings",
        label: "Time Slots Settings",
        icon: Settings,
        permission: "access/time-slots-settings"
      }
    ]
  },
  {
    title: "System",
    items: [
      {
        to: "/system/franchise",
        label: "Franchise",
        icon: Building2,
        permission: "access/franchise",
        marketplaceAdminOnly: true
      },
      {
        to: "/system/store",
        label: "Store",
        icon: Home,
        permission: "access/store"
      },
      {
        to: "/system/user",
        label: "Users",
        icon: Users,
        permission: "access/users"
      },
      {
        to: "/system/user-group",
        label: "User Group",
        icon: Users,
        permission: "access/user-group"
      },
      {
        to: "/system/sms-messages",
        label: "SMS Messages",
        icon: MessageCircle
      },
      {
        to: "/reports",
        label: "Reports",
        icon: BarChart3,
        permission: "access/reports"
      }
    ]
  }
];

export default function OCSidebar() {
  const auth = useAuth();
  const { isSuperAdmin, isMarketplaceAdmin } = useMarketplace();
  const navigate = useNavigate();
  const location = useLocation();

  // Laundry Agents (user_group_id 14) get the agent-only "My Deliveries" link.
  const LAUNDRY_AGENT_GROUP_ID = 14;
  const isLaundryAgent =
    Number(auth?.user?.user_group_id) === LAUNDRY_AGENT_GROUP_ID;

  const can = (permission) => {
    if (!permission) return true;
    // Super admins and marketplace super admins see every link (incl. the whole
    // Website section), regardless of granular access/* permissions.
    if (isSuperAdmin || isMarketplaceAdmin) return true;
    if (!auth || typeof auth.hasPermission !== "function") {
      return true;
    }
    return auth.hasPermission(permission);
  };

  function handleLogout() {
    auth.logout();
    navigate("/login", { replace: true });
  }

  return (
    <aside className="oc-sidebar">
      <div className="oc-brand">
        <img
          alt="WashyWash"
          src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'><circle cx='14' cy='14' r='12' fill='%2323a1d1'/></svg>"
        />
        <span className="oc-brand-text">WashyWash</span>
      </div>

      <nav className="oc-nav">
        {groups.map((g) => {
          const visibleItems = g.items.filter((it) => {
            // Items restricted to specific admin usernames (e.g. the card-payments
            // monitor for Mohammad Dev). Shown ONLY to those users.
            if (it.onlyUsers) return it.onlyUsers.includes(auth?.user?.username);
            if (it.marketplaceAdminOnly && !isMarketplaceAdmin) return false;
            // strictPermission is checked directly (NOT bypassed for admins) so a
            // dedicated permission like access/map-zones actually gates the item.
            if (it.strictPermission) {
              return typeof auth?.hasPermission === "function" && auth.hasPermission(it.strictPermission);
            }
            if (it.driverOrAdmin) return isLaundryAgent || can(it.permission);
            return it.agentOnly ? isLaundryAgent : can(it.permission);
          });
          if (visibleItems.length === 0) return null;

          return (
            <div key={g.title} className="oc-nav-group">
              <div className="group-title">{g.title}</div>
              <ul>
                {visibleItems.map((it) => {
                  // External launcher (e.g. "Edit Website (CMS)") — opens a new tab.
                  if (it.external) {
                    return (
                      <li key={it.label}>
                        <a
                          href="#"
                          role="button"
                          className="oc-nav-link"
                          style={{ textDecoration: "none", cursor: "pointer" }}
                          onClick={(e) => { e.preventDefault(); it.onClick(); }}
                        >
                          <span className="ico">
                            <it.icon size={16} strokeWidth={1.8} />
                          </span>
                          <span className="text">{it.label}</span>
                        </a>
                      </li>
                    );
                  }

                  const isActiveLink = () => {
                    const currentPath = location.pathname;
                    const searchParams = new URLSearchParams(location.search);

                    // If 'to' is exactly /orders, we want it active ONLY if status isn't 22
                    if (it.to === "/orders") {
                      return currentPath === "/orders" && searchParams.get("order_status_id") !== "22";
                    }

                    // If 'to' is /orders?order_status_id=22, we want it active ONLY if status IS 22
                    if (it.to === "/orders?order_status_id=22") {
                      return currentPath === "/orders" && searchParams.get("order_status_id") === "22";
                    }

                    // Standard logic for other links
                    if (it.to.includes("?")) {
                      const [path, search] = it.to.split("?");
                      const toParams = new URLSearchParams(search);
                      if (currentPath !== path) return false;
                      for (const [key, val] of toParams.entries()) {
                        if (searchParams.get(key) !== val) return false;
                      }
                      return true;
                    }
                    return currentPath === it.to || (it.to !== "/" && currentPath.startsWith(it.to + "/"));
                  };

                  const active = isActiveLink();

                  return (
                    <li key={it.to}>
                      <Link
                        to={it.to}
                        style={{ textDecoration: "none" }}
                        className={"oc-nav-link" + (active ? " active" : "")}
                        onClick={(e) => {
                          if (window.location.pathname === it.to) {
                            e.preventDefault();
                            navigate(it.to);
                          }
                        }}
                      >
                        <span className="ico">
                          <it.icon size={16} strokeWidth={1.8} />
                        </span>
                        <span className="text">{it.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="oc-sidebar-footer">
        <button className="oc-btn ghost full" onClick={handleLogout}>
          <span className="ico">
            <LogOut size={16} strokeWidth={1.8} />
          </span>
          <span className="text">Logout</span>
        </button>
      </div>
    </aside>
  );
}
